import { MagicWandIcon } from '@treams/treams-icons';
import { rgba } from 'polished';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import { Box, Flex } from 'reflexbox';
import styled, { keyframes } from 'styled-components';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import { Avatar } from './Avatar';
import { IconButton } from './IconButton';
import { Skeleton } from './Skeleton';
import { WorkingArea } from './WorkingArea';
import { Button } from './Button';

const Callout = styled(Flex)`
	color: ${(props) => props.theme.colors.primary};
	background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	border-radius: 24px;
	font-size: 16px;
	cursor: pointer;

	&:hover {
		background: ${(props) => rgba(props.theme.colors.primary, 0.15)};

		&:active {
			background: ${(props) => rgba(props.theme.colors.primary, 0.2)};
		}
	}
`;

const Container = styled(Box)`
	background: white;
	border-radius: 8px;
`;

const slideIn = keyframes`
    0% { opacity: 0; transform: translateY(10px); }
    100% { opacity: 1; transform: none; }
`;

const ChatBubble = styled(Box)<{ index: number }>`
	background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
	border-radius: 8px;
	color: ${(props) => props.theme.colors.primary};
	line-height: 20px;
	animation: 1s cubic-bezier(0, 1, 0, 1) ${slideIn};
	animation-delay: ${(props) => props.index * 1000}ms;
	animation-fill-mode: backwards;
`;

const TypingIndicatorBubble = styled(Flex)`
	background: ${(props) => props.theme.colors.whiteBlue};
	border-radius: 8px;
	line-height: 20px;
`;

function TypingIndicator() {
	return (
		<TypingIndicatorBubble p={3}>
			<Skeleton.Box width={6} height={6} style={{ borderRadius: 4 }} mr={1} />
			<Skeleton.Box width={6} height={6} style={{ borderRadius: 4 }} mr={1} />
			<Skeleton.Box width={6} height={6} style={{ borderRadius: 4 }} />
		</TypingIndicatorBubble>
	);
}

function ListItem(props: { text: string }) {
	return (
		<Flex alignItems="baseline">
			<Box width={20} flexGrow={0} flexShrink={0} mr={1} textAlign="right">
				{props.text.substring(0, props.text.indexOf('.'))}.
			</Box>
			<Box flexGrow={1} flexShrink={1}>
				{props.text.substring(props.text.indexOf('.') + 1)}
			</Box>
		</Flex>
	);
}

function MagicChat(props: { name: string; context: Record<string, string> }) {
	const { t } = useTranslation();
	const { language } = useLocalize();

	const [context, setContext] = useState(props.context);

	const { data } = graphql.useMagicQuery({
		variables: {
			name: props.name,
			context: Object.entries(context).map(([name, value]) => ({ name, value })),
			locale: language.id,
		},
	});

	return (
		<Box mt={2}>
			<Flex alignItems="top">
				<Box mr={2} mt="6px" flexGrow={0} flexShrink={0}>
					<Avatar
						user={
							{
								firstName: t('magic.identity.firstName'),
								lastName: t('magic.identity.lastName'),
							} as graphql.User
						}
						size={24}
					/>
				</Box>
				<Box>
					{data ? (
						data.magic.messages.map((message, i) => (
							<Flex mt={i === 0 ? 0 : 1} key={i}>
								<ChatBubble p={2} index={i}>
									{message.text.match(/^[0-9+]\./) ? (
										<ListItem text={message.text} />
									) : (
										message.text
									)}
								</ChatBubble>
							</Flex>
						))
					) : (
						<Box>
							<Flex>
								<ChatBubble p={2} index={1}>
									{t('magic.thinking')}
								</ChatBubble>
							</Flex>
							<Flex mt={1}>
								<TypingIndicator />
							</Flex>
						</Box>
					)}
					{!_.isEqual(props.context, context) && (
						<Flex mt={1}>
							<Flex flexGrow={1} flexShrink={1} mr={2}>
								<ChatBubble p={2} index={0}>
									{t('magic.changed.message')}
								</ChatBubble>
							</Flex>
							<Button
								type="primary"
								small
								title={t('magic.changed.button')}
								onClick={() => setContext(props.context)}
							/>
						</Flex>
					)}
				</Box>
			</Flex>
		</Box>
	);
}

export function Magic(
	props: PropsWithChildren<{
		name: string;
		context: Record<string, string>;
		button: string;
	}>,
) {
	const [active, setActive] = useState(false);

	return (
		<Box style={{ position: 'relative' }}>
			<div style={{ height: 1 }} />
			<Box mb={2} mx={-2} mt="-9px">
				<WorkingArea active={active} onDismiss={() => setActive(false)}>
					{(active) => (
						<Container p={2} px={2}>
							{props.children}
							{active && <MagicChat name={props.name} context={props.context} />}
						</Container>
					)}
				</WorkingArea>
			</Box>
			<Flex>
				<Callout p={2} pr={3} alignItems="center" onClick={() => setActive(true)}>
					<Box mr={2} flexShrink={0}>
						<IconButton icon={<MagicWandIcon />} />
					</Box>
					{props.button}
				</Callout>
			</Flex>
		</Box>
	);
}
